import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { ProtectedRoute } from '@/components/ui/protected-route';
import { PageLoading } from '@/components/ui/loading';
import { UserRole } from '@/types';

// Auth pages (eagerly loaded)
import { LoginPage } from '@/pages/auth/login';

// Lazy-loaded pages for code splitting
const ExecutiveDashboard = lazy(() =>
  import('@/pages/dashboard/executive').then((m) => ({ default: m.ExecutiveDashboard }))
);
const OperativeDashboard = lazy(() =>
  import('@/pages/dashboard/operative').then((m) => ({ default: m.OperativeDashboard }))
);
const AccountingDashboard = lazy(() =>
  import('@/pages/dashboard/accounting').then((m) => ({ default: m.AccountingDashboard }))
);

// Export por defecto, a diferencia del resto: esta pantalla no comparte nada con
// las protegidas y no se re-exporta desde ningún barrel.
const AssetVerificationPage = lazy(() => import('@/pages/public/asset-verification'));
const AssetListPage = lazy(() =>
  import('@/pages/assets/asset-list').then((m) => ({ default: m.AssetListPage }))
);
const AssetDetailPage = lazy(() =>
  import('@/pages/assets/asset-detail').then((m) => ({ default: m.AssetDetailPage }))
);
const AssetFormPage = lazy(() =>
  import('@/pages/assets/asset-form').then((m) => ({ default: m.AssetFormPage }))
);
const AssetImportPage = lazy(() =>
  import('@/pages/assets/asset-import').then((m) => ({ default: m.AssetImportPage }))
);

const DepreciationListPage = lazy(() =>
  import('@/pages/depreciation/depreciation-list').then((m) => ({
    default: m.DepreciationListPage,
  }))
);
const DepreciationCalculatePage = lazy(() =>
  import('@/pages/depreciation/depreciation-calculate').then((m) => ({
    default: m.DepreciationCalculatePage,
  }))
);

const MovementListPage = lazy(() =>
  import('@/pages/movements/movement-list').then((m) => ({ default: m.MovementListPage }))
);
const TransferListPage = lazy(() =>
  import('@/pages/movements/transfer-list').then((m) => ({ default: m.TransferListPage }))
);

const WorkOrderListPage = lazy(() =>
  import('@/pages/maintenance/work-order-list').then((m) => ({
    default: m.WorkOrderListPage,
  }))
);

const UserListPage = lazy(() =>
  import('@/pages/admin/user-list').then((m) => ({ default: m.UserListPage }))
);
const LocationListPage = lazy(() =>
  import('@/pages/admin/location-list').then((m) => ({ default: m.LocationListPage }))
);

const CategoryListPage = lazy(() =>
  import('@/pages/admin/category-list').then((m) => ({ default: m.CategoryListPage }))
);

const FormTemplateListPage = lazy(() =>
  import('@/pages/admin/form-template-list').then((m) => ({ default: m.FormTemplateListPage }))
);

const AuditLogPage = lazy(() =>
  import('@/pages/audit/audit-log').then((m) => ({ default: m.AuditLogPage }))
);

const ProfilePage = lazy(() =>
  import('@/pages/profile/profile').then((m) => ({ default: m.ProfilePage }))
);

const ReportIndexPage = lazy(() =>
  import('@/pages/reports/report-index').then((m) => ({ default: m.ReportIndexPage }))
);

const AssignTagsPage = lazy(() =>
  import('@/pages/rfid/assign-tags').then((m) => ({ default: m.AssignTagsPage }))
);
const RfidInventoryPage = lazy(() =>
  import('@/pages/rfid/inventory').then((m) => ({ default: m.RfidInventoryPage }))
);
const RfidDevicesPage = lazy(() =>
  import('@/pages/rfid/devices').then((m) => ({ default: m.RfidDevicesPage }))
);
const AssetMapPage = lazy(() =>
  import('@/pages/movements/asset-map').then((m) => ({ default: m.AssetMapPage }))
);
const MaintenancePlanListPage = lazy(() =>
  import('@/pages/maintenance/plan-list').then((m) => ({
    default: m.MaintenancePlanListPage,
  }))
);
const ReconciliationPage = lazy(() =>
  import('@/pages/audit/reconciliation').then((m) => ({ default: m.ReconciliationPage }))
);
const SystemConfigPage = lazy(() =>
  import('@/pages/admin/system-config').then((m) => ({ default: m.SystemConfigPage }))
);
const NotificationsListPage = lazy(() =>
  import('@/pages/notifications/notifications-list').then((m) => ({
    default: m.NotificationsListPage,
  }))
);
const LoanListPage = lazy(() =>
  import('@/pages/loans/loan-list').then((m) => ({ default: m.LoanListPage }))
);
const DisposalListPage = lazy(() =>
  import('@/pages/disposals/disposal-list').then((m) => ({ default: m.DisposalListPage }))
);
const AccountingEntriesPage = lazy(() =>
  import('@/pages/accounting/accounting-entries').then((m) => ({
    default: m.AccountingEntriesPage,
  }))
);
const NiifReportsPage = lazy(() =>
  import('@/pages/reports/niif-reports').then((m) => ({ default: m.NiifReportsPage }))
);

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

// Role groups for route protection — alineado a spec §3.2
const ALL_ROLES = Object.values(UserRole);
const ASSET_ROLES = [
  UserRole.SYSADMIN,
  UserRole.ASSET_ADMIN,
  UserRole.FIELD_OP,
  UserRole.STORE_MGR,
  UserRole.TECH,
  UserRole.AUDITOR,
];
// RFID_ROLES — sin AUDITOR: el auditor solo lee activos, no escribe RFID.
const RFID_ROLES = [
  UserRole.SYSADMIN,
  UserRole.ASSET_ADMIN,
  UserRole.FIELD_OP,
  UserRole.STORE_MGR,
  UserRole.TECH,
];
const ACCOUNTING_ROLES = [UserRole.SYSADMIN, UserRole.ACCOUNTANT, UserRole.AUDITOR];
const TRACE_ROLES = [
  UserRole.SYSADMIN,
  UserRole.ASSET_ADMIN,
  UserRole.FIELD_OP,
  UserRole.STORE_MGR,
  UserRole.AUDITOR,
];
const MAINTENANCE_ROLES = [UserRole.SYSADMIN, UserRole.ASSET_ADMIN, UserRole.TECH];
const ADMIN_ROLES = [UserRole.SYSADMIN];
// Reports: todos menos FIELD_OP (spec §3.2 — FIELD_OP solo tiene Trazabilidad)
const REPORT_ROLES = [
  UserRole.SYSADMIN,
  UserRole.ASSET_ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.AUDITOR,
  UserRole.STORE_MGR,
  UserRole.TECH,
];

export function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />

      {/*
        Verificación pública de etiqueta (ADR-003). Va aquí, fuera del
        ProtectedRoute, porque el QR lo escanea un operario con su móvil personal
        y sin sesión. Antes el QR apuntaba a /activos/:id, que sí está protegida:
        sólo funcionaba para quien ya tenía el web abierto, que es lo contrario
        de para qué se pega un QR en una máquina.
      */}
      <Route
        path="/q/:id"
        element={
          <LazyPage>
            <AssetVerificationPage />
          </LazyPage>
        }
      />

      {/* Access denied page */}
      <Route
        path="/sin-acceso"
        element={
          <div className="flex h-screen items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-destructive">403</h1>
              <p className="mt-2 text-lg text-muted-foreground">
                No tiene permisos para acceder a esta sección.
              </p>
              <a
                href="/"
                className="mt-4 inline-flex h-10 items-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Volver al inicio
              </a>
            </div>
          </div>
        }
      />

      {/* Protected routes with layout */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* Default redirect */}
        <Route index element={<Navigate to="/dashboard/operativo" replace />} />

        {/* Dashboards */}
        <Route
          path="dashboard/ejecutivo"
          element={
            <ProtectedRoute roles={ALL_ROLES}>
              <LazyPage>
                <ExecutiveDashboard />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="dashboard/operativo"
          element={
            <ProtectedRoute roles={ALL_ROLES}>
              <LazyPage>
                <OperativeDashboard />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="dashboard/contable"
          element={
            <ProtectedRoute roles={ACCOUNTING_ROLES}>
              <LazyPage>
                <AccountingDashboard />
              </LazyPage>
            </ProtectedRoute>
          }
        />

        {/* MOD-REG: Assets */}
        <Route
          path="activos"
          element={
            <ProtectedRoute roles={ASSET_ROLES}>
              <LazyPage>
                <AssetListPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="activos/nuevo"
          element={
            <ProtectedRoute roles={[UserRole.SYSADMIN, UserRole.ASSET_ADMIN]}>
              <LazyPage>
                <AssetFormPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="activos/importar"
          element={
            <ProtectedRoute roles={[UserRole.SYSADMIN, UserRole.ASSET_ADMIN]}>
              <LazyPage>
                <AssetImportPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="activos/:id"
          element={
            <ProtectedRoute roles={ASSET_ROLES}>
              <LazyPage>
                <AssetDetailPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="activos/:id/editar"
          element={
            <ProtectedRoute roles={[UserRole.SYSADMIN, UserRole.ASSET_ADMIN]}>
              <LazyPage>
                <AssetFormPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />

        {/* MOD-RFID */}
        <Route
          path="rfid/asignar"
          element={
            <ProtectedRoute roles={RFID_ROLES}>
              <LazyPage>
                <AssignTagsPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="rfid/inventario"
          element={
            <ProtectedRoute roles={RFID_ROLES}>
              <LazyPage>
                <RfidInventoryPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="rfid/dispositivos"
          element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <LazyPage>
                <RfidDevicesPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />

        {/* MOD-TRACE: Traceability */}
        <Route
          path="trazabilidad/movimientos"
          element={
            <ProtectedRoute roles={TRACE_ROLES}>
              <LazyPage>
                <MovementListPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="trazabilidad/traslados"
          element={
            <ProtectedRoute roles={TRACE_ROLES}>
              <LazyPage>
                <TransferListPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="trazabilidad/mapa"
          element={
            <ProtectedRoute roles={TRACE_ROLES}>
              <LazyPage>
                <AssetMapPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="trazabilidad/prestamos"
          element={
            <ProtectedRoute roles={TRACE_ROLES}>
              <LazyPage>
                <LoanListPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="activos/bajas"
          element={
            <ProtectedRoute roles={[UserRole.SYSADMIN, UserRole.ASSET_ADMIN, UserRole.ACCOUNTANT]}>
              <LazyPage>
                <DisposalListPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />

        {/* MOD-DEP: Depreciation */}
        <Route
          path="depreciacion"
          element={
            <ProtectedRoute roles={ACCOUNTING_ROLES}>
              <LazyPage>
                <DepreciationListPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="depreciacion/calcular"
          element={
            <ProtectedRoute roles={ACCOUNTING_ROLES}>
              <LazyPage>
                <DepreciationCalculatePage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="depreciacion/asientos"
          element={
            <ProtectedRoute roles={ACCOUNTING_ROLES}>
              <LazyPage>
                <AccountingEntriesPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />

        {/* MOD-MAINT: Maintenance */}
        <Route
          path="mantenimiento/ordenes"
          element={
            <ProtectedRoute roles={MAINTENANCE_ROLES}>
              <LazyPage>
                <WorkOrderListPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="mantenimiento/planes"
          element={
            <ProtectedRoute roles={MAINTENANCE_ROLES}>
              <LazyPage>
                <MaintenancePlanListPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />

        {/* MOD-AUDIT: Audit */}
        <Route
          path="auditoria/log"
          element={
            <ProtectedRoute roles={[UserRole.SYSADMIN, UserRole.AUDITOR]}>
              <LazyPage>
                <AuditLogPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="auditoria/conciliacion"
          element={
            <ProtectedRoute roles={[UserRole.SYSADMIN, UserRole.AUDITOR]}>
              <LazyPage>
                <ReconciliationPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />

        {/* MOD-REPORT: Reports (FIELD_OP excluido — spec §3.2) */}
        <Route
          path="reportes"
          element={
            <ProtectedRoute roles={REPORT_ROLES}>
              <LazyPage>
                <ReportIndexPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="reportes/niif"
          element={
            <ProtectedRoute roles={ACCOUNTING_ROLES}>
              <LazyPage>
                <NiifReportsPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />

        {/* MOD-ADMIN: Administration */}
        <Route
          path="admin/usuarios"
          element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <LazyPage>
                <UserListPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/ubicaciones"
          element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <LazyPage>
                <LocationListPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/categorias"
          element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <LazyPage>
                <CategoryListPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/formularios"
          element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <LazyPage>
                <FormTemplateListPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/configuracion"
          element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <LazyPage>
                <SystemConfigPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />

        {/* Profile */}
        <Route
          path="perfil"
          element={
            <ProtectedRoute roles={ALL_ROLES}>
              <LazyPage>
                <ProfilePage />
              </LazyPage>
            </ProtectedRoute>
          }
        />

        {/* Notifications (REQ-TRACE-004 / REQ-MAINT-004) */}
        <Route
          path="notificaciones"
          element={
            <ProtectedRoute roles={ALL_ROLES}>
              <LazyPage>
                <NotificationsListPage />
              </LazyPage>
            </ProtectedRoute>
          }
        />
      </Route>

      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="flex h-screen items-center justify-center">
            <div className="text-center">
              <h1 className="text-6xl font-bold text-muted-foreground">404</h1>
              <p className="mt-2 text-lg text-muted-foreground">
                Página no encontrada
              </p>
              <a
                href="/"
                className="mt-4 inline-flex h-10 items-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Volver al inicio
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  );
}